import "../../App.css";
import React, { useState } from "react";
import ReactSlider from "react-slider";
import { Country, State, City } from "country-state-city";
import { FaTimes } from "react-icons/fa";

const normalizeGender = (v) => (v === "Man" ? "Male" : v === "Woman" ? "Female" : v);

export default function SearchModal({
  initialAge = [18, 45],
  initialGender = "",
  initialLookingFor = "",
  initialCountry = "",
  initialState = "",
  initialCity = "",
  onSearchUpdate,
  onClose,
}) {
  const [ageRange, setAgeRange] = useState(initialAge);
  const [gender, setGender] = useState(normalizeGender(initialGender));
  const [lookingFor, setLookingFor] = useState(normalizeGender(initialLookingFor));
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [selectedState, setSelectedState] = useState(initialState);
  const [selectedCity, setSelectedCity] = useState(initialCity);

  const countries = Country.getAllCountries();
  const states = selectedCountry ? State.getStatesOfCountry(selectedCountry) : [];
  const cities =
    selectedCountry && selectedState
      ? City.getCitiesOfState(selectedCountry, selectedState)
      : [];

  const handleUpdate = () => {
    if (!gender || !lookingFor || ageRange.length !== 2) {
      return alert("Please choose gender, preference, and age range.");
    }
    onSearchUpdate({
      ageRange,
      gender,
      lookingFor,
      ...(selectedCountry && { country: selectedCountry }),
      ...(selectedState && { state: selectedState }),
      ...(selectedCity && { city: selectedCity }),
    });
    scrollToTop();
    onClose();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };  

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md overflow-hidden">
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Search</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FaTimes />
          </button>
        </div>

        {/* body */}
        <div className="p-6 space-y-6">
          {/* Gender & Looking For */}
          <div className="grid grid-cols-2 gap-4">
            <ToggleGroup label="I'm a" value={gender} onChange={setGender} />
            <ToggleGroup label="Looking for" value={lookingFor} onChange={setLookingFor} />
          </div>

          {/* Age Range */}
          <div>
            <label className="block mb-2 text-sm font-medium">Age</label>
            <div className="flex justify-between text-xs text-gray-600 mb-2">
              <span>{ageRange[0]} yrs</span>
              <span>{ageRange[1]} yrs</span>
            </div>
            <ReactSlider
min={18}
max={75}
value={ageRange}
onChange={setAgeRange}
pearling
minDistance={1}
className="custom-slider"
thumbClassName="custom-thumb"
renderTrack={(props, state) => {
  const { key, style, ...rest } = props;
  const colors = ["#d1d5db", "#3b82f6", "#d1d5db"];
  return (
    <div
      key={key}
      {...rest}
      style={{
        ...style,
        height: "4px",
        backgroundColor: colors[state.index],
        borderRadius: "4px",
      }}
    />
  );
}}
/>
          </div>

          {/* Location selects */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select
              label="Country"
              value={selectedCountry}
              onChange={(e) => {
                setSelectedCountry(e.target.value);
                setSelectedState("");
                setSelectedCity("");
              }}
              options={[{ value: "", label: "Any" }, ...countries.map((c) => ({ value: c.isoCode, label: c.name }))]}
            />
            <Select
              label="State"
              value={selectedState}
              onChange={(e) => {
                setSelectedState(e.target.value);
                setSelectedCity("");
              }}
              options={[{ value: "", label: "Any" }, ...states.map((s) => ({ value: s.isoCode, label: s.name }))]}
              disabled={!selectedCountry}
            />
            <Select
              label="City"
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              options={[{ value: "", label: "Any" }, ...cities.map((c) => ({ value: c.name, label: c.name }))]}
              disabled={!selectedState}
            />
          </div>
        </div>

        {/* footer */}
        <div className="flex justify-end px-6 py-4 bg-gray-50 space-x-3">
          <button
            onClick={onClose}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Search
          </button>
        </div>
      </div>
    </div>
  );
}

// small helper for the two toggle groups
function ToggleGroup({ label, value, onChange }) {
  return (
    <div>
      <p className="mb-2 text-sm font-medium">{label}</p>
      <div className="flex gap-2">
        {["Male", "Female"].map((opt) => (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={`flex-1 text-sm py-2 rounded ${
              value === opt
                ? "bg-blue-600 text-white"
                : "bg-white border border-gray-300 text-gray-700"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

// small helper for selects
function Select({ label, options, value, onChange, disabled }) {
  return (
    <div>
      <label className="block mb-1 text-sm font-medium">{label}</label>
      <select
        className={`w-full px-3 py-2 border rounded ${
          disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"
        }`}
        value={value}
        onChange={onChange}
        disabled={disabled}
      >
        {options.map((o) => (
          <option key={o.value || "__"} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
